import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, CalendarKPIStrip } from '@anyemp/ui-kit';
import { talents, statuses as statusesApi, users, playbooks as playbooksApi } from '../services/apiService';
import { Loader } from '../components/UI/LoadingSpinner';
import { dateTypes } from '../utils/dateTypeUtils';
import { getTalentTypeOptions, getTalentTypeColor, getTalentTypeLabel } from '../utils/talentTypeUtils';
import { getStatusStyles } from '../utils/colorUtils';
import { fmtTzDate, fmtTzTime, fmtTzDateHuman, fmtTzDateTimeHuman } from '../utils/dateFormatUtils';
import { useAuth } from '../hooks/useAuth';
import {
  DEFAULT_SHOW_VALUES,
  SHOW_ACTIVE_STAGES,
  SHOW_FILTER_OPTIONS,
  SHOW_STEP_DEADLINES,
  buildImportantDateEvent,
  buildLegendMetrics,
  createLatestRequestGuard,
  getCalendarSourceVisibility,
  isCalendarFilterResetTarget,
  restoreShowDefaultsAfterReset,
  toInclusiveNavigationRange,
} from '../utils/calendarPageUtils';

const TIME_DATE_TYPES = new Set(['interview', 'follow_up']);

const DATE_TYPE_ICONS = {
  interview: '🎙️',
  start_work: '🚀',
  last_contact: '📞',
  fired: '⛔',
  left: '🚪',
  follow_up: '🔄',
  video_added: '🎥',
  birthday: '🎂',
};

const STAGE_RANGE_CATEGORY = SHOW_ACTIVE_STAGES;
const STAGE_STEP_CATEGORY = SHOW_STEP_DEADLINES;

const STAGE_META = {
  [STAGE_RANGE_CATEGORY]: {
    label: 'Active stage',
    short: 'Stage',
    color: '#6366F1',
    bg: 'rgba(99, 102, 241, 0.12)',
    icon: '🧭',
  },
  [STAGE_STEP_CATEGORY]: {
    label: 'Stage step due',
    short: 'Step',
    color: '#0EA5E9',
    bg: 'rgba(14, 165, 233, 0.14)',
    icon: '📌',
  },
};

const CATEGORY_META = {};
dateTypes.forEach((dt) => {
  CATEGORY_META[dt.value] = {
    label: dt.label,
    short: dt.label,
    color: dt.color,
    bg: dt.color + '1A',
    icon: DATE_TYPE_ICONS[dt.value],
  };
});
Object.assign(CATEGORY_META, STAGE_META);


const fmtDate = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const getWideRange = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth() - 6, 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 7, 0);
  return { startDate: fmtDate(start), endDate: fmtDate(end) };
};

const EventSidebarDetail = ({ event }) => {
  const { kind } = event.data || {};
  if (kind === STAGE_RANGE_CATEGORY) return <StageRangeDetail event={event} />;
  if (kind === STAGE_STEP_CATEGORY) return <StageStepDetail event={event} />;
  return <ImportantDateDetail event={event} />;
};

const EventSidebarItem = ({ event }) => {
  const meta = CATEGORY_META[event.category] || {};
  const { kind, profession, playbookName } = event.data || {};
  const subtitle = kind === STAGE_RANGE_CATEGORY
    ? playbookName
    : (profession?.name && profession?.similar_name
        ? `${profession.name} ~ ${profession.similar_name}`
        : profession?.name);

  return (
    <>
      <span className="ui-calendar__sidebar-day-item-bar" />
      <div className="ui-calendar__sidebar-day-item-body">
        <span className="ui-calendar__sidebar-day-item-title">{event.title}</span>
        {subtitle && (
          <span className="text-xs text-gray-500 dark:text-gray-400 truncate">{subtitle}</span>
        )}
        <span className="ui-calendar__sidebar-day-item-meta">
          <span style={{ color: meta.color }}>{meta.label || event.category}</span>
          {event.time && <span> · {event.time}</span>}
        </span>
      </div>
      <span className="ui-calendar__sidebar-day-item-arrow">›</span>
    </>
  );
};

const ImportantDateDetail = ({ event }) => {
  const {
    description,
    talentId,
    talentType,
    status,
    managerId,
    manager,
    profession,
    dateValue,
    occurrenceDate,
    age,
    yearsOfService,
    hasTime,
  } = event.data || {};

  const whenValue = occurrenceDate || dateValue;
  const whenLabel = whenValue
    ? (hasTime ? fmtTzDateTimeHuman(dateValue) : fmtTzDateHuman(whenValue))
    : null;

  return (
    <div className="flex flex-col gap-4 text-sm">
      {whenLabel && (
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-1">
            When
          </p>
          <p className="text-gray-800 dark:text-gray-200 font-medium">{whenLabel}</p>
        </div>
      )}

      {Number.isInteger(age) && (
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-1">Age</p>
          <p className="text-gray-800 dark:text-gray-200 font-medium">{age} years old</p>
        </div>
      )}

      {Number.isInteger(yearsOfService) && (
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-1">Service</p>
          <p className="text-gray-800 dark:text-gray-200 font-medium">
            {yearsOfService} {yearsOfService === 1 ? 'year' : 'years'}
          </p>
        </div>
      )}

      {description && (
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-1">
            Description
          </p>
          <p className="text-gray-700 dark:text-gray-300 leading-relaxed">{description}</p>
        </div>
      )}

      <div>
        <p className="text-xs font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-1">
          Talent
        </p>
        <Link
          to={`/talents/${talentId}`}
          target='_blank'
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="font-medium text-blue-600 dark:text-blue-400 hover:underline"
        >
          {event.title}
        </Link>
      </div>

      {profession?.name && (
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-1">
            Profession
          </p>
          <p className="text-gray-700 dark:text-gray-300">{profession.name}</p>
          {profession.similar_name && (
            <p className="text-xs text-purple-600 dark:text-purple-400 mt-0.5">~ {profession.similar_name}</p>
          )}
          {profession.department && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{profession.department}</p>
          )}
        </div>
      )}

      {talentType && (
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-1">
            Type
          </p>
          <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${getTalentTypeColor(talentType)}`}>
            {getTalentTypeLabel(talentType)}
          </span>
        </div>
      )}

      {status && (
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-1">
            Status
          </p>
          <span
            className="inline-flex px-2 py-1 text-xs font-semibold rounded-full"
            style={getStatusStyles(status.color, { backgroundColorOpacity: 0.15, borderOpacity: 0.4, textOpacity: 1 })}
          >
            {status.name}
          </span>
        </div>
      )}

      {(manager || managerId) && (
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-1">
            Manager
          </p>
          {manager ? (
            <div className="flex items-center gap-2">
              {manager.avatar && (
                <img
                  src={manager.avatar}
                  alt={manager.name}
                  className="w-6 h-6 rounded-full object-cover shrink-0"
                />
              )}
              <span className="text-gray-700 dark:text-gray-300 font-medium">{manager.name}</span>
            </div>
          ) : (
            <p className="text-gray-500 dark:text-gray-400 text-xs">{managerId}</p>
          )}
        </div>
      )}
    </div>
  );
};

const StageRangeDetail = ({ event }) => {
  const { playbookName, startedAt, endsAt, isOverdue, talents = [] } = event.data || {};

  return (
    <div className="flex flex-col gap-4 text-sm">
      <div>
        <p className="text-xs font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-1">Playbook</p>
        <p className="text-gray-800 dark:text-gray-200 font-medium">{playbookName || '—'}</p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-1">Started</p>
          <p className="text-gray-700 dark:text-gray-300">{fmtTzDateHuman(startedAt)}</p>
        </div>
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-1">Ends</p>
          <p className="text-gray-700 dark:text-gray-300">{fmtTzDateHuman(endsAt)}</p>
        </div>
      </div>

      {isOverdue && (
        <div className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 w-fit">
          ⚠️ Overdue
        </div>
      )}

      <div>
        <p className="text-xs font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-2">
          Talents{talents.length ? ` (${talents.length})` : ''}
        </p>
        <div className="flex flex-col gap-3">
          {talents.map((t) => (
            <div key={t.instanceId ?? t.id} className="rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800/60 p-2.5">
              <div className="flex items-center justify-between gap-2">
                <Link
                  to={`/talents/${t.id}#stages`}
                  target='_blank'
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="font-medium text-blue-600 dark:text-blue-400 hover:underline truncate"
                >
                  {t.fullName || 'Talent'}
                </Link>
                {t.talentType && (
                  <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium shrink-0 ${getTalentTypeColor(t.talentType)}`}>
                    {getTalentTypeLabel(t.talentType)}
                  </span>
                )}
              </div>
              {t.profession?.name && (
                <div className="mt-1">
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {t.profession.name}{t.profession.department ? ` · ${t.profession.department}` : ''}
                  </p>
                  {t.profession.similar_name && (
                    <p className="text-xs text-purple-600 dark:text-purple-400 truncate">~ {t.profession.similar_name}</p>
                  )}
                </div>
              )}
              <div className="flex items-center gap-2 mt-2">
                <div className="flex-1 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div className="h-full bg-primary-600 dark:bg-primary-500" style={{ width: `${Math.max(0, Math.min(100, t.progressPercent || 0))}%` }} />
                </div>
                <span className="text-xs text-gray-500 dark:text-gray-400 w-10 text-right">{t.progressPercent || 0}%</span>
                {t.isOverdue && <span className="text-xs" title="Overdue">⚠️</span>}
              </div>
              {(t.manager || t.managerId) && (
                <div className="flex items-center gap-2 mt-2 pt-2 border-t border-gray-100 dark:border-gray-700">
                  {t.manager?.avatar && (
                    <img src={t.manager.avatar} alt={t.manager.name} className="w-5 h-5 rounded-full object-cover shrink-0" />
                  )}
                  <span className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {t.manager?.name || t.managerId}
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const StageStepDetail = ({ event }) => {
  const { talentId, talentType, playbookName, stepTitle, stepDescription, links, isOverdue, manager, managerId, profession } = event.data || {};

  return (
    <div className="flex flex-col gap-4 text-sm">
      <div>
        <p className="text-xs font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-1">Step</p>
        <p className="text-gray-800 dark:text-gray-200 font-medium">{stepTitle || '—'}</p>
        {stepDescription && (
          <p className="text-gray-600 dark:text-gray-400 leading-relaxed mt-1">{stepDescription}</p>
        )}
      </div>

      {playbookName && (
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-1">Playbook</p>
          <p className="text-gray-700 dark:text-gray-300">{playbookName}</p>
        </div>
      )}

      <div>
        <p className="text-xs font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-1">Talent</p>
        <Link
          to={`/talents/${talentId}#stages`}
          target='_blank'
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="font-medium text-blue-600 dark:text-blue-400 hover:underline"
        >
          {event.title}
        </Link>
        {talentType && (
          <div className="mt-1">
            <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${getTalentTypeColor(talentType)}`}>
              {getTalentTypeLabel(talentType)}
            </span>
          </div>
        )}
        {profession?.name && (
          <div className="mt-1">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {profession.name}{profession.department ? ` · ${profession.department}` : ''}
            </p>
            {profession.similar_name && (
              <p className="text-xs text-purple-600 dark:text-purple-400">~ {profession.similar_name}</p>
            )}
          </div>
        )}
      </div>

      {Array.isArray(links) && links.length > 0 && (
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-1">Links</p>
          <div className="flex flex-col gap-1">
            {links.map((l, i) => (
              <a
                key={i}
                href={l.url || l}
                target='_blank'
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="text-xs text-blue-600 dark:text-blue-400 hover:underline truncate"
              >
                {l.label || l.url || l}
              </a>
            ))}
          </div>
        </div>
      )}

      {isOverdue && (
        <div className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 w-fit">
          ⚠️ Overdue
        </div>
      )}

      {(manager || managerId) && (
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-1">Manager</p>
          {manager ? (
            <div className="flex items-center gap-2">
              {manager.avatar && <img src={manager.avatar} alt={manager.name} className="w-6 h-6 rounded-full object-cover shrink-0" />}
              <span className="text-gray-700 dark:text-gray-300 font-medium">{manager.name}</span>
            </div>
          ) : (
            <p className="text-gray-500 dark:text-gray-400 text-xs">{managerId}</p>
          )}
        </div>
      )}
    </div>
  );
};

const CalendarPage = () => {
  const { hasPermission } = useAuth();

  const allowedTalentTypes = [
    hasPermission('talent-service', 'candidate', 'list') && 'candidate',
    hasPermission('talent-service', 'trainee',   'list') && 'trainee',
    hasPermission('talent-service', 'employee',  'list') && 'employee',
    hasPermission('talent-service', 'presale',   'list') && 'presale',
  ].filter(Boolean);

  const [events, setEvents] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [refetching, setRefetching] = useState(false);
  const [error, setError] = useState(null);
  const [filterSections, setFilterSections] = useState([]);
  const [filterValues, setFilterValues] = useState({ show: [...DEFAULT_SHOW_VALUES] });
  const allowedStatusesRef = useRef([]);
  const filterDebounceRef = useRef(null);
  const filterValuesRef = useRef({});
  const skipFirstNavigateRef = useRef(true);
  const resetRequestedRef = useRef(false);
  const loadRequestGuardRef = useRef(createLatestRequestGuard());

  filterValuesRef.current = filterValues;

  useEffect(() => {
    loadFilterData();
    loadCalendarData({ show: [...DEFAULT_SHOW_VALUES] }, true);
  }, []);

  const loadFilterData = async () => {
    try {
      const typesToLoad = allowedTalentTypes;

      const [statusResults, usersRes, playbooksRes] = await Promise.all([
        Promise.all(typesToLoad.map((t) => statusesApi.getStatusesByType(t, { isCommon: 1 }))),
        users.getAllUsers(),
        playbooksApi.list({ isActive: true }).catch(() => ({ data: [] })),
      ]);

      const mergedStatuses = statusResults.flatMap((r) => r?.data || []);
      const uniqueStatuses = Array.from(new Map(mergedStatuses.map((s) => [String(s.id), s])).values());

      allowedStatusesRef.current = uniqueStatuses;

      const managerList = usersRes.data?.users || [];
      const playbookList = playbooksRes?.data || [];

      const talentTypeOptions = getTalentTypeOptions().filter(
        (t) => allowedTalentTypes.length === 0 || allowedTalentTypes.includes(t.value)
      );

      const sections = [
        {
          id: 'show',
          title: 'Show',
          type: 'pills',
          options: SHOW_FILTER_OPTIONS,
        },
        {
          id: 'importantDatesRange',
          title: 'Important Dates Range',
          type: 'daterange',
        },
        {
          id: 'dateTypes',
          title: 'Date Types',
          type: 'pills',
          options: dateTypes.map((dt) => ({ value: dt.value, label: dt.label })),
        },
        playbookList.length > 0 && {
          id: 'playbookIds',
          title: 'Playbooks',
          type: 'select',
          multiple: true,
          searchable: true,
          options: playbookList.map((p) => ({ value: String(p.id), label: p.name })),
        },
        talentTypeOptions.length > 1 && {
          id: 'talentTypes',
          title: 'Talent Types',
          type: 'pills',
          options: talentTypeOptions.map((t) => ({ value: t.value, label: t.label })),
        },
        {
          id: 'statusIds',
          title: 'Statuses',
          type: 'select',
          multiple: true,
          searchable: true,
          options: uniqueStatuses.map((s) => ({ value: String(s.id), label: s.name })),
        },
        {
          id: 'managerIds',
          title: 'Managers',
          type: 'select',
          multiple: true,
          searchable: true,
          options: managerList.map((u) => ({
            value: String(u.id),
            label: u.name || u.email || String(u.id),
          })),
        },
      ].filter(Boolean);

      setFilterSections(sections);
    } catch (err) {
      console.error('Error loading filter data:', err);
    }
  };

  const loadCalendarData = useCallback(async (filters = {}, isInitial = false) => {
    const requestId = loadRequestGuardRef.current.start();
    isInitial ? setInitialLoading(true) : setRefetching(true);
    setError(null);

    try {
      const range = filters.importantDatesRange;
      let startDate, endDate;
      if (range?.start && range?.end) {
        startDate = fmtDate(range.start);
        endDate = fmtDate(range.end);
      } else if (range?.start) {
        startDate = fmtDate(range.start);
        const e = new Date(range.start);
        e.setMonth(e.getMonth() + 2);
        endDate = fmtDate(e);
      } else {
        ({ startDate, endDate } = getWideRange());
      }

      const params = { startDate, endDate };
      if (filters.dateTypes?.length > 0) params.dateTypes = filters.dateTypes.join(',');
      if (filters.talentTypes?.length > 0) {
        params.talentTypes = filters.talentTypes.join(',');
      } else if (allowedTalentTypes.length === 1) {
        params.talentTypes = allowedTalentTypes[0];
      }
      if (filters.statusIds?.length > 0) params.statusIds = filters.statusIds.join(',');
      if (filters.managerIds?.length > 0) params.managerIds = filters.managerIds.join(',');

      const {
        showImportantDates,
        showActiveStages: showRanges,
        showStepDeadlines: showSteps,
        needStages,
      } = getCalendarSourceVisibility(filters.show ?? DEFAULT_SHOW_VALUES);

      const stageParams = { startDate, endDate };
      if (filters.playbookIds?.length > 0) stageParams.playbookIds = filters.playbookIds.join(',');
      if (params.talentTypes) stageParams.talentTypes = params.talentTypes;
      if (params.managerIds) stageParams.managerIds = params.managerIds;

      const [response, stageResponse] = await Promise.all([
        showImportantDates ? talents.getCalendarData(params) : Promise.resolve(null),
        needStages ? playbooksApi.getCalendarData(stageParams).catch((err) => {
          console.error('Error loading stage calendar data:', err);
          return null;
        }) : Promise.resolve(null),
      ]);

      if (response && !response.success) {
        if (loadRequestGuardRef.current.isCurrent(requestId)) {
          setError('Failed to load calendar data');
        }
        return;
      }

      const flatEvents = [];

      if (response) {
        Object.entries(response.data?.data ?? response.data).forEach(([, items]) => {
          items.forEach((item) => {
            flatEvents.push(buildImportantDateEvent(item, {
              formatDate: fmtTzDate,
              formatTime: fmtTzTime,
              timeDateTypes: TIME_DATE_TYPES,
            }));
          });
        });
      }

      const stageData = stageResponse?.data?.data ?? stageResponse?.data ?? stageResponse;
      if (stageData) {
        if (showRanges && Array.isArray(stageData.rangeBars)) {
          stageData.rangeBars.forEach((rb) => {
            const startStr = fmtTzDate(rb.started_at);
            const endStr = fmtTzDate(rb.ends_at);
            const talents = Array.isArray(rb.talents) ? rb.talents : [];
            const count = rb.talent_count ?? talents.length;
            const title = count === 1
              ? `${talents[0]?.full_name || 'Talent'} — ${rb.playbook_name || 'Stage'}`
              : `${rb.playbook_name || 'Stage'} — ${count}`;
            flatEvents.push({
              id: `pr-${rb.id}`,
              category: STAGE_RANGE_CATEGORY,
              title,
              date: startStr,
              endDate: endStr,
              urgent: rb.is_overdue || false,
              data: {
                kind: STAGE_RANGE_CATEGORY,
                groupId: rb.id,
                playbookId: rb.playbook_id,
                playbookName: rb.playbook_name,
                startedAt: rb.started_at,
                endsAt: rb.ends_at,
                isOverdue: rb.is_overdue,
                talentCount: count,
                talents: talents.map((t) => ({
                  instanceId: t.instance_id,
                  id: t.id,
                  fullName: t.full_name,
                  talentType: t.talent_type,
                  managerId: t.manager_id,
                  manager: t.manager || null,
                  progressPercent: t.progress_percent,
                  isOverdue: t.is_overdue,
                  profession: t.profession || null,
                })),
              },
            });
          });
        }
        if (showSteps && Array.isArray(stageData.stepPoints)) {
          stageData.stepPoints.forEach((sp) => {
            flatEvents.push({
              id: `ps-${sp.id}`,
              category: STAGE_STEP_CATEGORY,
              title: `${sp.talent?.full_name || 'Talent'}: ${sp.title || 'Step'}`,
              date: fmtTzDate(sp.due_date),
              urgent: sp.is_overdue || false,
              data: {
                kind: STAGE_STEP_CATEGORY,
                stepInstanceId: sp.id,
                instanceId: sp.instance_id,
                talentId: sp.talent_id,
                talentType: sp.talent?.talent_type,
                playbookId: sp.playbook_id,
                playbookName: sp.playbook_name,
                stepTitle: sp.title,
                stepDescription: sp.description,
                links: sp.links || [],
                isOverdue: sp.is_overdue,
                managerId: sp.talent?.manager_id || null,
                manager: sp.talent?.manager || null,
                profession: sp.talent?.profession || null,
              },
            });
          });
        }
      }

      if (loadRequestGuardRef.current.isCurrent(requestId)) {
        setEvents(flatEvents);
      }
    } catch (err) {
      console.error('Error loading calendar data:', err);
      if (loadRequestGuardRef.current.isCurrent(requestId)) {
        setError('Error loading calendar data');
      }
    } finally {
      if (loadRequestGuardRef.current.isCurrent(requestId)) {
        setInitialLoading(false);
        setRefetching(false);
      }
    }
  }, []);

  const handleNavigate = useCallback(({ start, end }) => {
    if (skipFirstNavigateRef.current) {
      skipFirstNavigateRef.current = false;
      return;
    }
    const newValues = toInclusiveNavigationRange(filterValuesRef.current, { start, end });
    setFilterValues(newValues);
    clearTimeout(filterDebounceRef.current);
    filterDebounceRef.current = setTimeout(() => loadCalendarData(newValues), 150);
  }, [loadCalendarData]);

  const handleFilterValuesChange = useCallback((newValues) => {
    const normalizedValues = restoreShowDefaultsAfterReset(newValues, resetRequestedRef.current);
    resetRequestedRef.current = false;
    const prevTalentTypes = filterValues.talentTypes ?? [];
    const nextTalentTypes = normalizedValues.talentTypes ?? [];

    const talentTypesChanged =
      prevTalentTypes.length !== nextTalentTypes.length ||
      nextTalentTypes.some((t) => !prevTalentTypes.includes(t));

    if (talentTypesChanged) {
      const baseStatuses = allowedStatusesRef.current || [];
      const statusList = nextTalentTypes.length > 0
        ? baseStatuses.filter((st) => nextTalentTypes.includes(st.talent_type))
        : baseStatuses;

      const validIds = new Set(statusList.map((st) => String(st.id)));
      const cleanedStatusIds = (normalizedValues.statusIds ?? []).filter((id) => validIds.has(id));

      const updatedValues = { ...normalizedValues, statusIds: cleanedStatusIds };
      setFilterValues(updatedValues);

      setFilterSections((prev) =>
        prev.map((s) =>
          s.id === 'statusIds'
            ? { ...s, options: statusList.map((st) => ({ value: String(st.id), label: st.name })) }
            : s
        )
      );

      clearTimeout(filterDebounceRef.current);
      filterDebounceRef.current = setTimeout(() => loadCalendarData(updatedValues), 150);
    } else {
      setFilterValues(normalizedValues);
      clearTimeout(filterDebounceRef.current);
      filterDebounceRef.current = setTimeout(() => loadCalendarData(normalizedValues), 150);
    }
  }, [filterValues, loadCalendarData]);

  const handleCalendarClickCapture = useCallback((event) => {
    if (isCalendarFilterResetTarget(event.target)) {
      resetRequestedRef.current = true;
    }
  }, []);

  if (initialLoading) {
    return (
      <div className="flex justify-center items-center py-20">
        <Loader size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-100 dark:bg-red-900/20 border border-red-400 dark:border-red-700 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg">
        {error}
      </div>
    );
  }

  return (
    <div
      className="calendar-viewport relative -mx-4 -my-6 h-[calc(100dvh-4.0625rem)] min-w-0 max-w-[calc(100%+2rem)] overflow-hidden sm:-mx-6 sm:max-w-[calc(100%+3rem)] md:-mx-8 md:max-w-[calc(100%+4rem)] lg:h-dvh"
      onClickCapture={handleCalendarClickCapture}
    >
      <title>Calendar | Talent Service</title>

      {refetching && (
        <div className="absolute inset-0 z-20 bg-white/40 dark:bg-gray-900/40 backdrop-blur-[1px]" />
      )}

      <Calendar
        events={events}
        categoryMeta={CATEGORY_META}
        defaultView="month"
        showSearch={false}
        style={{ height: '100%' }}
        filterSections={filterSections}
        filterValues={filterValues}
        onFilterValuesChange={handleFilterValuesChange}
        onNavigate={handleNavigate}
        renderSidebarDetail={(event) => <EventSidebarDetail event={event} />}
        renderSidebarItem={(event) => <EventSidebarItem event={event} />}
        renderKPI={() => {
          const metrics = buildLegendMetrics({
            showValues: filterValues.show ?? DEFAULT_SHOW_VALUES,
            dateTypes,
            selectedDateTypes: filterValues.dateTypes ?? [],
            stageMeta: STAGE_META,
          });
          return <CalendarKPIStrip metrics={metrics} />;
        }}
      />
    </div>
  );
};

export default CalendarPage;
